import React, { useEffect, useRef, useState } from "react";
import { apiClient } from "../../utils/http";

const MIN_SEARCH = { customer_name: 2, mobile: 3 };

/* Step 1 — customer & basic order info, with suggestions from the store's existing customers. */
export default function StepCustomer({ form, update, active }) {
  const [suggestFor, setSuggestFor] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const search = (field, value) => {
    clearTimeout(timer.current);
    if (value.trim().length < MIN_SEARCH[field]) {
      setSuggestions([]);
      return;
    }
    timer.current = setTimeout(() => {
      apiClient
        .get("customers", { params: { search: value.trim() } })
        .then((res) => {
          setSuggestFor(field);
          setSuggestions(res.data.customers || []);
        })
        .catch(() => setSuggestions([]));
    }, 300);
  };

  // Typing a different name/mobile unlinks any customer picked earlier.
  const handleLookupInput = (field, value) => {
    update({ [field]: value, store_customer_id: null });
    search(field, value);
  };

  const pickCustomer = (customer) => {
    update({
      store_customer_id: customer.id,
      customer_name: customer.name || "",
      contact_person: customer.contact_person_name || form.contact_person,
      mobile: customer.contact_number || "",
      email: customer.email || form.email,
      billing_address: customer.address || form.billing_address,
    });
    setSuggestions([]);
  };

  const closeSuggestions = () => setTimeout(() => setSuggestions([]), 150);

  const renderSuggestions = (field) =>
    suggestFor === field && suggestions.length > 0 && (
      <div className="customer-suggestions">
        {suggestions.map((customer) => (
          <button
            type="button"
            key={customer.id}
            className="customer-suggestion"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => pickCustomer(customer)}
          >
            <span className="customer-name">{customer.name}</span>
            <span className="contact-sub">
              {[customer.contact_person_name, customer.contact_number, customer.email].filter(Boolean).join(" · ")}
            </span>
          </button>
        ))}
      </div>
    );

  return (
    <div className={`wizard-step-panel ${active ? "active" : ""}`} id="step-panel-1">
      <div className="panel-section-title">
        <span className="section-badge">1</span> Customer &amp; Basic Order Information
      </div>

      <div className="form-grid grid-2">
        <div className="form-group">
          <label htmlFor="input-job_no">
            Job Order No. <span className="req">*</span>
          </label>
          <input
            type="text"
            id="input-job_no"
            placeholder="e.g. JO-2026-001"
            maxLength={30}
            value={form.job_no}
            onChange={(e) => update({ job_no: e.target.value })}
          />
        </div>
        <div className="form-group">
          <label htmlFor="input-order_date">
            Date <span className="req">*</span>
          </label>
          <input type="date" id="input-order_date" value={form.order_date} onChange={(e) => update({ order_date: e.target.value })} />
        </div>
      </div>

      <div className="form-grid grid-2">
        <div className="form-group">
          <label htmlFor="input-customer_name">
            Customer / company name: <span className="req">*</span>
          </label>
          <div className="autocomplete-wrap">
            <input
              type="text"
              id="input-customer_name"
              placeholder="Full name or company name"
              autoComplete="off"
              maxLength={255}
              value={form.customer_name}
              onChange={(e) => handleLookupInput("customer_name", e.target.value)}
              onBlur={closeSuggestions}
            />
            {renderSuggestions("customer_name")}
          </div>
          {form.store_customer_id && <span className="linked-customer-hint">✓ Existing customer</span>}
        </div>
        <div className="form-group">
          <label htmlFor="input-contact_person">Contact Person</label>
          <input
            type="text"
            id="input-contact_person"
            placeholder="Primary contact name"
            maxLength={255}
            value={form.contact_person}
            onChange={(e) => update({ contact_person: e.target.value })}
          />
        </div>
      </div>

      <div className="form-grid grid-2">
        <div className="form-group">
          <label htmlFor="input-mobile">
            Mobile Number <span className="req">*</span>
          </label>
          <div className="autocomplete-wrap">
            <input
              type="tel"
              id="input-mobile"
              placeholder="+971 50 123 4567"
              autoComplete="off"
              maxLength={30}
              value={form.mobile}
              onChange={(e) => handleLookupInput("mobile", e.target.value)}
              onBlur={closeSuggestions}
            />
            {renderSuggestions("mobile")}
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="input-email">Email Address</label>
          <input
            type="email"
            id="input-email"
            placeholder="client@example.com"
            maxLength={255}
            value={form.email}
            onChange={(e) => update({ email: e.target.value })}
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="input-billing_address">Billing Address</label>
        <textarea
          id="input-billing_address"
          rows="2"
          placeholder="Full billing address"
          value={form.billing_address}
          onChange={(e) => update({ billing_address: e.target.value })}
        ></textarea>
      </div>

      <div className="form-group checkbox-toggle-group">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={form.same_as_billing}
            onChange={(e) => update({ same_as_billing: e.target.checked })}
          />
          <span>Delivery address is same as billing address</span>
        </label>
      </div>

      {!form.same_as_billing && (
        <div className="form-group">
          <label htmlFor="input-delivery_address">Delivery Address</label>
          <textarea
            id="input-delivery_address"
            rows="2"
            placeholder="Full delivery address"
            value={form.delivery_address}
            onChange={(e) => update({ delivery_address: e.target.value })}
          ></textarea>
        </div>
      )}
    </div>
  );
}
